import { WidgetComponent, WidgetConfig } from "@/types";
import { createRoot, Root } from "react-dom/client";
import { AppQueryClientProvider } from "@/lib/react-query";

class Widget {
  private static instances: Map<string, Widget> = new Map();
  private container: HTMLElement;
  private config: WidgetConfig;
  private root: Root;
  private Component: WidgetComponent;

  constructor(
    container: HTMLElement,
    config: WidgetConfig,
    Component: WidgetComponent,
  ) {
    this.container = container;
    this.config = config;
    this.Component = Component;
    this.root = createRoot(container);
    this.render();
  }

  static init(config: WidgetConfig, Component: WidgetComponent): Widget {
    if (Widget.instances.has(config.widgetId)) {
      console.error(`Widget with ID ${config.widgetId} already exists`);
    }

    let container = document.getElementById(config.widgetId) as HTMLElement;

    if (!container) {
      container = document.createElement("div");
      container.id = config.widgetId;
      document.body.appendChild(container);
    }

    // Ensure Tailwind styles apply by scoping under the required important selector
    if (!container.classList.contains("widget-container")) {
      container.classList.add("widget-container");
    }

    const instance = new Widget(container, config, Component);
    Widget.instances.set(config.widgetId, instance);
    return instance;
  }

  static getInstance(widgetId: string): Widget | undefined {
    return Widget.instances.get(widgetId);
  }

  getContainer(): HTMLElement {
    return this.container;
  }

  getConfig(): WidgetConfig {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<WidgetConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig,
    };
    this.render();
  }

  private render(): void {
    try {
      this.root.render(
        <AppQueryClientProvider>
          <this.Component {...this.config} />
        </AppQueryClientProvider>,
      );
    } catch (error) {
      console.error(`Error rendering widget ${this.config.widgetId}:`, error);
      this.renderError();
    }
  }

  private renderError(): void {
    this.root.render(
      <div className="widget-error">
        <p>Error loading widget</p>
      </div>,
    );
  }

  destroy(): void {
    try {
      this.root.unmount();
      this.container.remove();
      Widget.instances.delete(this.config.widgetId);
    } catch (error) {
      console.error(`Error destroying widget ${this.config.widgetId}:`, error);
    }
  }

  static destroyAll(): void {
    Widget.instances.forEach((widget) => widget.destroy());
    Widget.instances.clear();
  }
}

export default Widget;
